//purchaseorder_reducer.js
const initial_state = {
  purchaseorders: [],
};

const getPoKey = (po) => {
  if (!po || typeof po !== "object") return "";
  for (const v of [po.POID, po.poid, po.id, po.purchaseOrderId, po.PurchaseOrder_Id]) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s && s !== "null" && s !== "undefined") return s;
  }
  return "";
};

const itemCount = (po) => {
  const items = po?.purchaseOrderItems || po?.PurchaseOrderItems;
  return Array.isArray(items) ? items.length : 0;
};

const isWeakPoStatus = (status) => {
  const s = String(status || "").trim().toUpperCase();
  return !s || s === "DRAFT" || s === "NULL";
};

const CLIENT_UPSERT_TTL_MS = 120000; // keep just-created POs if an in-flight list sync omits them

const purchaseOrderReducer = (state = initial_state, action) => {
  switch (action.type) {
    case "GET_PURCHASE_ORDERS": {
      const remoteRaw = action.purchaseorders;
      const remote = Array.isArray(remoteRaw)
        ? remoteRaw
        : remoteRaw && typeof remoteRaw === "object"
          ? [remoteRaw]
          : [];

      const localByKey = new Map();
      (state.purchaseorders || []).forEach((po) => {
        const key = getPoKey(po);
        if (key) localByKey.set(key, po);
      });

      const remoteKeys = new Set();
      const now = Date.now();
      const mergedRemote = remote.map((r) => {
        const key = getPoKey(r);
        if (key) remoteKeys.add(key);
        const local = key ? localByKey.get(key) : null;
        if (!local) return r;
        // Prefer whichever side has line items (list sync often returns headers only)
        const preferLocalItems = itemCount(local) > itemCount(r);
        const localIsFresh =
          typeof local._clientUpsertAt === "number" &&
          now - local._clientUpsertAt < CLIENT_UPSERT_TTL_MS;
        // Do not let a slow/stale withoutDetails sync wipe a just-updated status
        // (e.g. RECEIVED after GRN) from syncPurchaseOrderIntoRedux.
        const status =
          localIsFresh && local.status
            ? local.status
            : r.status || local.status;
        return {
          ...local,
          ...r,
          POID: key,
          id: key,
          poid: key,
          status,
          purchaseOrderItems: preferLocalItems
            ? local.purchaseOrderItems
            : r.purchaseOrderItems || local.purchaseOrderItems || [],
          _clientUpsertAt: localIsFresh
            ? local._clientUpsertAt
            : local._clientUpsertAt || r._clientUpsertAt,
        };
      });

      const extras = [];
      localByKey.forEach((po, key) => {
        if (remoteKeys.has(key)) return;
        const at = po._clientUpsertAt;
        if (typeof at === "number" && now - at < CLIENT_UPSERT_TTL_MS) {
          extras.push(po);
        }
      });

      return {
        ...state,
        purchaseorders: extras.length ? [...extras, ...mergedRemote] : mergedRemote,
        purchaseOrdersLoaded: true,
      };
    }

    /** Insert or replace one PO — never used on API failure. */
    case "UPSERT_PURCHASE_ORDER": {
      const po = action.purchaseorder;
      if (!po) return state;
      const key = getPoKey(po);
      if (!key) return state;
      const list = state.purchaseorders || [];
      const idx = list.findIndex((o) => getPoKey(o) === key);
      const mergePo = (existing, incoming) => {
        const preferIncomingItems = itemCount(incoming) > 0;
        const preferExistingItems = itemCount(existing) > 0 && !preferIncomingItems;
        const pickId = (po) => {
          for (const v of [po?.Vendor_Id, po?.vendor_Id, po?.vendorId]) {
            if (v == null) continue;
            const s = String(v).trim();
            if (s && s.toLowerCase() !== "null" && s.toLowerCase() !== "undefined") return s;
          }
          return "";
        };
        const incomingVendor = pickId(incoming);
        const existingVendor = pickId(existing);
        const vendorId = incomingVendor || existingVendor;
        const incomingStatus =
          incoming?.status != null &&
          String(incoming.status).trim() &&
          String(incoming.status).toLowerCase() !== "null"
            ? String(incoming.status).trim()
            : "";
        const existingStatus =
          existing?.status != null &&
          String(existing.status).trim() &&
          String(existing.status).toLowerCase() !== "null"
            ? String(existing.status).trim()
            : "";
        const status =
          !isWeakPoStatus(incomingStatus)
            ? incomingStatus
            : !isWeakPoStatus(existingStatus)
              ? existingStatus
              : incomingStatus || existingStatus || "Draft";
        return {
          ...existing,
          ...incoming,
          POID: key,
          id: key,
          poid: key,
          status,
          ...(vendorId
            ? { Vendor_Id: vendorId, vendor_Id: vendorId }
            : {}),
          purchaseOrderItems: preferIncomingItems
            ? incoming.purchaseOrderItems
            : preferExistingItems
              ? existing.purchaseOrderItems
              : incoming.purchaseOrderItems || existing.purchaseOrderItems || [],
        };
      };
      const next =
        idx >= 0
          ? list.map((o, i) => (i === idx ? mergePo(o, po) : o))
          : [po, ...list];
      return { ...state, purchaseorders: next };
    }

    /** Patch fields on an existing PO by id (status, lines, etc.). */
    case "PATCH_PURCHASE_ORDER": {
      const key = String(action.id ?? "");
      const patch = action.patch || {};
      if (!key || key === "null" || key === "undefined") return state;
      const list = state.purchaseorders || [];
      return {
        ...state,
        purchaseorders: list.map((o) =>
          getPoKey(o) === key ? { ...o, ...patch } : o
        ),
      };
    }

    case "CLEAR_PURCHASE_ORDER_DATA":
      return initial_state;
    default:
      return state;
  }
};
export default purchaseOrderReducer;
